import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  SchemaBuilder,
  jsonSchemaToFields,
  fieldsToJsonSchema,
} from '@/components/workflows/schema-builder'
import { VariableMapper } from '@/components/workflows/variable-mapper'
import { useWorkflowStore } from '@/stores/workflow-store'
import { useOrganizationStore } from '@/stores/organization-store'
import type { SchemaField, NodeVariableMapping, WorkflowStatus } from '@/schemas/workflow'
import { FlowTab, useFlowTabPersistence } from './workflow-detail-flow-tab'
import { HistoryTab } from '@/components/workflows/lifecycle/history-tab'
import { PublishControls } from '@/components/workflows/lifecycle/publish-controls'
import { EditPublishedBanner } from '@/components/workflows/lifecycle/edit-published-banner'
import { StatusConsole } from '@/components/workflows/lifecycle/status-console'
import { DefaultLlmCard } from '@/components/workflows/default-llm-card'

interface SettingsFormData {
  name: string
  description: string
}

export function WorkflowDetailPage() {
  const { t } = useTranslation('workflows')
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const { currentProject } = useOrganizationStore()
  const {
    currentWorkflow,
    loading,
    fetchWorkflow,
    updateWorkflow,
    deleteWorkflow,
    setFlowFromWorkflow,
  } = useWorkflowStore()

  // Settings form
  const { register, handleSubmit, reset } = useForm<SettingsFormData>()
  const [settingsSaving, setSettingsSaving] = useState(false)

  // Schema state
  const [inputFields, setInputFields] = useState<SchemaField[]>([])
  const [outputFields, setOutputFields] = useState<SchemaField[]>([])
  const [variableMappings, setVariableMappings] = useState<NodeVariableMapping[]>([])
  const [schemaSaving, setSchemaSaving] = useState(false)

  // Status state
  const [statusSaving, setStatusSaving] = useState(false)

  // Flow tab
  const [activeTab, setActiveTab] = useState('settings')
  const prevTabRef = useRef(activeTab)
  const { handleTabChange } = useFlowTabPersistence()

  const onTabChange = useCallback((value: string) => {
    handleTabChange(value, prevTabRef.current)
    prevTabRef.current = value
    setActiveTab(value)
  }, [handleTabChange])

  // Fetch workflow on mount
  useEffect(() => {
    if (currentProject?.id && workflowId) {
      fetchWorkflow(currentProject.id, workflowId)
    }
  }, [currentProject?.id, workflowId, fetchWorkflow])

  // Sync form and flow canvas when workflow loads
  useEffect(() => {
    if (currentWorkflow) {
      reset({
        name: currentWorkflow.name,
        description: currentWorkflow.description || '',
      })
      setInputFields(jsonSchemaToFields(currentWorkflow.input_schema))
      setOutputFields(jsonSchemaToFields(currentWorkflow.output_schema))
      // Variable mappings stored in settings
      const mappings = (currentWorkflow.settings?.variable_mappings as NodeVariableMapping[]) || []
      setVariableMappings(mappings)
      // Hydrate React Flow canvas from API data
      setFlowFromWorkflow(currentWorkflow)
    }
  }, [currentWorkflow, reset, setFlowFromWorkflow])

  // Handlers
  const handleSaveSettings = useCallback(
    async (data: SettingsFormData) => {
      if (!currentProject?.id || !workflowId) return
      setSettingsSaving(true)
      try {
        await updateWorkflow(currentProject.id, workflowId, {
          name: data.name,
          description: data.description || null,
        })
        toast.success(t('toasts.settingsSaved'))
      } catch {
        toast.error(t('toasts.settingsError'))
      } finally {
        setSettingsSaving(false)
      }
    },
    [currentProject?.id, workflowId, updateWorkflow, t]
  )

  const handleSaveSchema = useCallback(async () => {
    if (!currentProject?.id || !workflowId) return
    setSchemaSaving(true)
    try {
      await updateWorkflow(currentProject.id, workflowId, {
        input_schema: fieldsToJsonSchema(inputFields),
        output_schema: fieldsToJsonSchema(outputFields),
        variable_mappings: variableMappings,
      })
      toast.success(t('toasts.schemaSaved'))
    } catch {
      toast.error(t('toasts.schemaError'))
    } finally {
      setSchemaSaving(false)
    }
  }, [
    currentProject?.id,
    workflowId,
    inputFields,
    outputFields,
    variableMappings,
    updateWorkflow,
    t,
  ])

  const handleToggleStatus = useCallback(async () => {
    if (!currentProject?.id || !workflowId || !currentWorkflow) return
    setStatusSaving(true)
    const newStatus: WorkflowStatus =
      currentWorkflow.status === 'active' ? 'inactive' : 'active'
    try {
      await updateWorkflow(currentProject.id, workflowId, { status: newStatus })
      toast.success(t('toasts.statusUpdated'))
    } catch {
      toast.error(t('toasts.statusError'))
    } finally {
      setStatusSaving(false)
    }
  }, [currentProject?.id, workflowId, currentWorkflow, updateWorkflow, t])

  const handleDelete = useCallback(async () => {
    if (!currentProject?.id || !workflowId) return
    if (!window.confirm(t('detail.deleteConfirm'))) return
    try {
      await deleteWorkflow(currentProject.id, workflowId)
      toast.success(t('toasts.deleted'))
      navigate('/workflows')
    } catch {
      toast.error(t('toasts.deleteError'))
    }
  }, [currentProject?.id, workflowId, deleteWorkflow, navigate, t])

  if (loading || !currentWorkflow) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-700/50 rounded animate-pulse" />
        <div className="h-64 bg-gray-700/30 rounded-2xl animate-pulse" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="text-gray-400 hover:text-gray-200"
            onClick={() => navigate('/workflows')}
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t('detail.backToWorkflows')}
          </Button>
          <h1 className="text-2xl font-bold text-gray-100">
            {currentWorkflow.name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {currentProject?.id && workflowId && (
            <PublishControls
              projectId={currentProject.id}
              workflowId={workflowId}
              workflow={currentWorkflow}
              onPublished={() => setActiveTab('history')}
            />
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            onClick={handleDelete}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            {t('detail.deleteButton')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="settings">
            {t('detail.tabs.settings')}
          </TabsTrigger>
          <TabsTrigger value="schema">
            {t('detail.tabs.schema')}
          </TabsTrigger>
          <TabsTrigger value="flow">
            {t('flow.tabLabel', 'Fluxo')}
          </TabsTrigger>
          <TabsTrigger value="status">
            {t('detail.tabs.status')}
          </TabsTrigger>
          <TabsTrigger value="history">
            {t('lifecycle.tabLabel')}
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          {currentProject?.id && (
            <DefaultLlmCard
              workflow={currentWorkflow}
              projectId={currentProject.id}
            />
          )}
          {currentWorkflow.status === 'active' && (
            <EditPublishedBanner
              workflowId={currentWorkflow.id}
              currentVersion={currentWorkflow.version}
            />
          )}
          <Card variant="glass" className="p-6">
            <form
              onSubmit={handleSubmit(handleSaveSettings)}
              className="space-y-6 max-w-xl"
            >
              <div className="space-y-2">
                <Label htmlFor="settings-name" className="text-gray-300">
                  {t('detail.settings.nameLabel')}
                </Label>
                <Input
                  id="settings-name"
                  className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50"
                  {...register('name')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings-description" className="text-gray-300">
                  {t('detail.settings.descriptionLabel')}
                </Label>
                <Textarea
                  id="settings-description"
                  className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50 min-h-[100px]"
                  {...register('description')}
                />
              </div>

              <div className="flex items-center gap-4">
                <div className="space-y-1">
                  <Label className="text-gray-400 text-sm">
                    {t('detail.settings.patternLabel')}
                  </Label>
                  <Badge variant="outline" className="border-gray-700/50 text-gray-300">
                    {t(`patterns.${currentWorkflow.pattern_type}.name`)}
                  </Badge>
                </div>
                <div className="space-y-1">
                  <Label className="text-gray-400 text-sm">
                    {t('detail.settings.versionLabel')}
                  </Label>
                  <div className="text-sm text-gray-300">
                    v{currentWorkflow.version}
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                variant="action-indigo"
                disabled={settingsSaving}
              >
                {settingsSaving ? '...' : t('detail.settings.saveButton')}
              </Button>
            </form>
          </Card>
        </TabsContent>

        {/* Schema Tab */}
        <TabsContent value="schema">
          <Card variant="glass" className="p-6 space-y-8">
            <SchemaBuilder
              fields={inputFields}
              onChange={setInputFields}
              title={t('detail.schema.inputTitle')}
            />

            <div className="border-t border-gray-700/30" />

            <SchemaBuilder
              fields={outputFields}
              onChange={setOutputFields}
              title={t('detail.schema.outputTitle')}
            />

            <div className="border-t border-gray-700/30" />

            <VariableMapper
              nodes={currentWorkflow.nodes}
              mappings={variableMappings}
              onChange={setVariableMappings}
            />

            <Button
              variant="action-indigo"
              onClick={handleSaveSchema}
              disabled={schemaSaving}
            >
              {schemaSaving ? '...' : t('detail.schema.saveButton')}
            </Button>
          </Card>
        </TabsContent>

        {/* Flow Tab */}
        <TabsContent value="flow">
          {currentWorkflow.status === 'active' && (
            <EditPublishedBanner
              workflowId={currentWorkflow.id}
              currentVersion={currentWorkflow.version}
            />
          )}
          <FlowTab />
        </TabsContent>

        {/* Status Tab */}
        <TabsContent value="status">
          <StatusConsole
            workflow={currentWorkflow}
            saving={statusSaving}
            onToggle={handleToggleStatus}
          />
        </TabsContent>

        {/* History Tab (Phase 03) */}
        <TabsContent value="history">
          {currentProject?.id && workflowId && (
            <HistoryTab
              projectId={currentProject.id}
              workflowId={workflowId}
              onPublish={() => {
                document.getElementById('publish-cta')?.click()
              }}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
